import { PageContainer } from "@/components/layout/PageContainer";
import { Helmet } from "react-helmet";
import { Shield, Lock, FileCheck, Users, Database, Monitor } from "lucide-react";

const ComplianceSecurity = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Compliance & Security - Scienza Health</title>
        <meta name="description" content="Enterprise-grade security and comprehensive compliance with healthcare regulations including HIPAA, SOC 2, and FDA requirements." />
      </Helmet>
      
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Compliance & Security
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Enterprise-grade security and comprehensive compliance framework ensuring the highest standards of data protection and regulatory adherence.
            </p>
          </div>

          {/* Security Framework */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Security Framework</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-card border rounded-lg p-6 text-center">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">End-to-End Encryption</h3>
                <p className="text-muted-foreground">
                  All data is encrypted in transit and at rest using AES-256 encryption standards with secure key management.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-6 text-center">
                <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Zero Trust Architecture</h3>
                <p className="text-muted-foreground">
                  Multi-factor authentication and role-based access controls ensure secure access to all platform resources.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-6 text-center">
                <Monitor className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">24/7 Monitoring</h3>
                <p className="text-muted-foreground">
                  Continuous security monitoring with real-time threat detection and automated incident response.
                </p>
              </div>
            </div>
          </div>

          {/* Compliance Standards */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Compliance Standards</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-card border rounded-lg p-8">
                <div className="flex items-center mb-4">
                  <FileCheck className="h-8 w-8 text-primary mr-3" />
                  <h3 className="text-2xl font-semibold">HIPAA Compliance</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Complete Business Associate Agreements (BAA)
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Administrative, physical, and technical safeguards
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Comprehensive audit logging and access controls
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Employee training and security awareness programs
                  </li>
                </ul>
              </div>

              <div className="bg-card border rounded-lg p-8">
                <div className="flex items-center mb-4">
                  <Database className="h-8 w-8 text-primary mr-3" />
                  <h3 className="text-2xl font-semibold">SOC 2 Type II</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Security, availability, and confidentiality controls
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Processing integrity and privacy protections
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Annual third-party security assessments
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Continuous monitoring and improvement processes
                  </li>
                </ul>
              </div>

              <div className="bg-card border rounded-lg p-8">
                <div className="flex items-center mb-4">
                  <Shield className="h-8 w-8 text-primary mr-3" />
                  <h3 className="text-2xl font-semibold">FDA Registration</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    FDA registered medical device platform
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Quality management system compliance
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Clinical validation and safety protocols
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Post-market surveillance and reporting
                  </li>
                </ul>
              </div>

              <div className="bg-card border rounded-lg p-8">
                <div className="flex items-center mb-4">
                  <Users className="h-8 w-8 text-primary mr-3" />
                  <h3 className="text-2xl font-semibold">Additional Standards</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    GDPR compliance for international operations
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    ISO 27001 information security management
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    NIST Cybersecurity Framework implementation
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Regular penetration testing and vulnerability assessments
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Protection */}
          <div className="text-center bg-muted rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-4">Data Protection Commitment</h2>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
              We are committed to maintaining the highest standards of data protection and privacy. Our comprehensive security 
              framework ensures that patient data is always protected, access is properly controlled, and all regulatory 
              requirements are met or exceeded.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <div className="bg-card border rounded px-4 py-2 text-sm font-medium">HIPAA Compliant</div>
              <div className="bg-card border rounded px-4 py-2 text-sm font-medium">SOC 2 Type II</div>
              <div className="bg-card border rounded px-4 py-2 text-sm font-medium">FDA Registered</div>
              <div className="bg-card border rounded px-4 py-2 text-sm font-medium">ISO 27001</div>
              <div className="bg-card border rounded px-4 py-2 text-sm font-medium">GDPR Ready</div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ComplianceSecurity;